<template>
  <div class="doc-partner">
    <!-- 页头 -->
    <div class="page-head">
      <div class="page-head-left">
        <h2 class="page-title">伙伴证书管理</h2>
        <p class="page-desc">维护合作伙伴的企业资质证书，及时掌握有效期与续期提醒</p>
      </div>
      <div class="page-head-right">
        <el-button type="primary" icon="el-icon-plus" @click="openAdd">新增证书</el-button>
      </div>
    </div>

    <!-- 概要 -->
    <div class="summary-bar">
      <div class="summary-item">
        <span class="summary-num">{{ certList.length }}</span>
        <span class="summary-label">证书总数</span>
      </div>
      <div class="summary-item">
        <span class="summary-num green">{{ countByStatus('valid') }}</span>
        <span class="summary-label">有效</span>
      </div>
      <div class="summary-item">
        <span class="summary-num orange">{{ countByStatus('warning') }}</span>
        <span class="summary-label">即将到期</span>
      </div>
      <div class="summary-item">
        <span class="summary-num red">{{ countByStatus('expired') }}</span>
        <span class="summary-label">已过期</span>
      </div>
      <div class="summary-tip">
        <i class="el-icon-info"></i> 有效期不足 90 天视为即将到期，请及时安排续期
      </div>
    </div>

    <!-- 证书列表 -->
    <div class="panel">
      <div class="panel-head">
        <h3>证书列表</h3>
        <span class="panel-note">共 {{ certList.length }} 项</span>
      </div>
      <div class="panel-body">
        <el-table :data="certList" stripe size="small" style="width: 100%" class="cert-table">
          <el-table-column label="证书" min-width="240">
            <template slot-scope="scope">
              <div class="cert-info">
                <div class="cert-badge" :class="'badge-' + scope.row.typeTag">
                  <i :class="certTypeIcon(scope.row.type)"></i>
                </div>
                <div class="cert-main">
                  <div class="cert-name">{{ scope.row.name }}</div>
                  <div class="cert-sub">{{ scope.row.partner }} · {{ scope.row.certNo }}</div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="partner" label="伙伴名称" width="140">
            <template slot-scope="scope">
              <span class="partner-name">{{ scope.row.partner }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="type" label="证书类型" min-width="180" show-overflow-tooltip>
          </el-table-column>
          <el-table-column prop="certNo" label="证书编号" min-width="170" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="cert-no">{{ scope.row.certNo }}</span>
            </template>
          </el-table-column>
          <el-table-column label="有效期" width="200">
            <template slot-scope="scope">
              <div class="validity">
                <span>{{ scope.row.validStart }}</span>
                <i class="el-icon-right"></i>
                <span>{{ scope.row.validEnd }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="110" align="center">
            <template slot-scope="scope">
              <el-tag :type="statusTagType(scope.row)" effect="light" size="small">
                {{ statusText(scope.row) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="130" align="center">
            <template slot-scope="scope">
              <el-button type="text" icon="el-icon-view" @click="showDetail(scope.row)">查看</el-button>
              <el-button type="text" class="del-btn" icon="el-icon-delete" @click="removeCert(scope.row)">删除</el-button>
            </template>
          </el-table-column>
          <template slot="empty">
            <div class="table-empty">
              <i class="el-icon-medal"></i>
              <p>暂无证书，点击右上角「新增证书」添加</p>
            </div>
          </template>
        </el-table>
      </div>
    </div>

    <!-- 新增证书 -->
    <el-dialog title="新增证书" :visible.sync="addVisible" width="560px" :close-on-click-modal="false" append-to-body>
      <el-form ref="addForm" :model="addForm" :rules="addRules" label-width="88px">
        <el-form-item label="伙伴名称" prop="partner">
          <el-select v-model="addForm.partner" placeholder="请选择伙伴公司" filterable style="width: 100%">
            <el-option label="云擎" value="云擎"></el-option>
            <el-option label="若优" value="若优"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="证书类型" prop="type">
          <el-input v-model="addForm.type" placeholder="如：ISO 9001 / CMMI Level 3"></el-input>
        </el-form-item>
        <el-form-item label="证书编号" prop="certNo">
          <el-input v-model="addForm.certNo" placeholder="请输入证书编号"></el-input>
        </el-form-item>
        <el-form-item label="生效日期" prop="validStart">
          <el-date-picker v-model="addForm.validStart" type="date" placeholder="选择生效日期" value-format="yyyy-MM-dd" style="width: 100%"></el-date-picker>
        </el-form-item>
        <el-form-item label="到期日期" prop="validEnd">
          <el-date-picker v-model="addForm.validEnd" type="date" placeholder="选择到期日期" value-format="yyyy-MM-dd" style="width: 100%"></el-date-picker>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="addVisible = false">取 消</el-button>
        <el-button type="primary" :loading="saving" @click="submitAdd">保 存</el-button>
      </div>
    </el-dialog>

    <!-- 证书详情 -->
    <el-dialog title="证书详情" :visible.sync="detailVisible" width="520px" append-to-body>
      <div v-if="currentCert" class="cert-detail">
        <div class="detail-head">
          <div class="cert-badge large" :class="'badge-' + currentCert.typeTag">
            <i :class="certTypeIcon(currentCert.type)"></i>
          </div>
          <div>
            <div class="detail-name">{{ currentCert.name }}</div>
            <div class="detail-org">{{ currentCert.partner }}</div>
          </div>
          <el-tag :type="statusTagType(currentCert)" effect="light">{{ statusText(currentCert) }}</el-tag>
        </div>
        <div class="detail-grid">
          <div class="detail-item">
            <div class="detail-label">证书类型</div>
            <div class="detail-value">{{ currentCert.type }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">证书编号</div>
            <div class="detail-value">{{ currentCert.certNo }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">生效日期</div>
            <div class="detail-value">{{ currentCert.validStart }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">到期日期</div>
            <div class="detail-value">{{ currentCert.validEnd }}</div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
const INIT_CERTS = [
  { name: 'ISO 9001 质量管理体系', partner: '云擎', type: 'ISO 9001', certNo: 'ISO-2024-08821', validStart: '2024-01-15', validEnd: '2027-01-14', typeTag: 'blue' },
  { name: 'CMMI 能力成熟度认证', partner: '云擎', type: 'CMMI Level 3', certNo: 'CMMI3-2024-0523', validStart: '2024-03-20', validEnd: '2027-03-19', typeTag: 'purple' },
  { name: '信息安全等级保护', partner: '云擎', type: '信息安全等级保护', certNo: 'DJCP-2023-0915', validStart: '2023-09-28', validEnd: '2025-09-27', typeTag: 'orange' },
  { name: '高新技术企业证书', partner: '云擎', type: '高新技术企业', certNo: 'GR202336000102', validStart: '2023-12-10', validEnd: '2026-12-09', typeTag: 'green' },
  { name: '软件企业认定证书', partner: '若优', type: '软件企业认定', certNo: 'RQ-2024-1103', validStart: '2024-05-08', validEnd: '2026-05-07', typeTag: 'cyan' },
  { name: 'ISO 27001 信息安全管理体系', partner: '若优', type: 'ISO 27001', certNo: 'ISO27001-2025-0112', validStart: '2025-01-06', validEnd: '2028-01-05', typeTag: 'blue' }
]

export default {
  name: 'DocumentPartner',
  data() {
    return {
      certList: INIT_CERTS.map(c => ({ ...c })),
      addVisible: false,
      saving: false,
      detailVisible: false,
      currentCert: null,
      addForm: {
        partner: '云擎',
        type: '',
        certNo: '',
        validStart: '',
        validEnd: ''
      },
      addRules: {
        partner: [{ required: true, message: '请选择伙伴名称', trigger: 'change' }],
        type: [{ required: true, message: '请输入证书类型', trigger: 'blur' }],
        certNo: [{ required: true, message: '请输入证书编号', trigger: 'blur' }],
        validStart: [{ required: true, message: '请选择生效日期', trigger: 'change' }],
        validEnd: [{ required: true, message: '请选择到期日期', trigger: 'change' }]
      }
    }
  },
  methods: {
    certStatus(c) {
      const end = new Date(c.validEnd).getTime()
      const now = Date.now()
      if (end < now) return 'expired'
      if (end - now < 90 * 24 * 3600 * 1000) return 'warning'
      return 'valid'
    },
    statusText(c) {
      const map = { valid: '有效', warning: '即将到期', expired: '已过期' }
      return map[this.certStatus(c)]
    },
    statusTagType(c) {
      const map = { valid: 'success', warning: 'warning', expired: 'danger' }
      return map[this.certStatus(c)]
    },
    countByStatus(status) {
      return this.certList.filter(c => this.certStatus(c) === status).length
    },
    certTypeIcon(type) {
      if (!type) return 'el-icon-medal'
      if (type.indexOf('ISO') !== -1) return 'el-icon-files'
      if (type.indexOf('CMMI') !== -1) return 'el-icon-star-on'
      if (type.indexOf('等级保护') !== -1) return 'el-icon-lock'
      if (type.indexOf('高新技术') !== -1) return 'el-icon-lightning'
      if (type.indexOf('软件企业') !== -1) return 'el-icon-connection'
      return 'el-icon-medal'
    },
    openAdd() {
      this.addForm = { partner: '云擎', type: '', certNo: '', validStart: '', validEnd: '' }
      this.addVisible = true
      this.$nextTick(() => this.$refs.addForm && this.$refs.addForm.clearValidate())
    },
    submitAdd() {
      this.$refs.addForm.validate(valid => {
        if (!valid) return
        this.saving = true
        setTimeout(() => {
          const typeTags = ['blue', 'purple', 'orange', 'green', 'cyan']
          this.certList.unshift({
            name: this.addForm.type + ' 证书',
            partner: this.addForm.partner,
            type: this.addForm.type,
            certNo: this.addForm.certNo,
            validStart: this.addForm.validStart,
            validEnd: this.addForm.validEnd,
            typeTag: typeTags[this.certList.length % typeTags.length]
          })
          this.saving = false
          this.addVisible = false
          this.$message.success('证书已添加')
        }, 300)
      })
    },
    showDetail(cert) {
      this.currentCert = cert
      this.detailVisible = true
    },
    removeCert(cert) {
      this.$confirm(`确定删除证书「${cert.name}」吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.certList = this.certList.filter(c => c !== cert)
        this.$message.success('证书已删除')
      }).catch(() => {})
    }
  }
}
</script>

<style scoped lang="scss">
.doc-partner {
  padding: 4px 0 20px;
}

.page-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-title {
  font-size: 22px;
  font-weight: 600;
  color: #1f2d3d;
}
.page-desc {
  margin-top: 6px;
  font-size: 13px;
  color: #909399;
}

/* 概要条 */
.summary-bar {
  display: flex;
  align-items: center;
  gap: 36px;
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 12px;
  padding: 16px 24px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.summary-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.summary-num {
  font-size: 24px;
  font-weight: 700;
  color: #1f2d3d;
  font-variant-numeric: tabular-nums;
}
.summary-num.green { color: #36cf63; }
.summary-num.orange { color: #fa8c16; }
.summary-num.red { color: #f56c6c; }
.summary-label {
  font-size: 13px;
  color: #909399;
}
.summary-tip {
  margin-left: auto;
  font-size: $font-size-base;
  color: #b0b6c0;
}
.summary-tip i {
  color: #fa8c16;
  margin-right: 4px;
}

.panel {
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 12px;
  overflow: hidden;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 22px;
  border-bottom: 1px solid #f2f3f5;
}
.panel-head h3 {
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
}
.panel-head h3::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 14px;
  border-radius: 2px;
  background: #409eff;
  margin-right: 8px;
  vertical-align: -2px;
}
.panel-note {
  font-size: $font-size-base;
  color: #b0b6c0;
}
.panel-body { padding: 18px 22px 8px; }

/* 证书行 */
.cert-info {
  display: flex;
  align-items: center;
  gap: 12px;
}
.cert-badge {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}
.cert-badge.large {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  font-size: 24px;
}
.badge-blue { background: #e6f2ff; color: #409eff; }
.badge-purple { background: #f3ecfb; color: #722ed1; }
.badge-orange { background: #fff3e6; color: #fa8c16; }
.badge-green { background: #e9f9ef; color: #36cf63; }
.badge-cyan { background: #e8f8f8; color: #13c2c2; }

.cert-main {
  min-width: 0;
}
.cert-name {
  font-size: 13px;
  font-weight: 600;
  color: #1f2d3d;
}
.cert-sub {
  margin-top: 3px;
  font-size: $font-size-base;
  color: #b0b6c0;
}
.partner-name {
  font-weight: 500;
  color: #409eff;
}
.cert-no {
  color: #606266;
  font-family: Consolas, Monaco, monospace;
  font-size: $font-size-base;
}
.validity {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: $font-size-base;
  color: #606266;
}
.validity i {
  color: #c0c4cc;
  font-size: $font-size-md;
}
.del-btn {
  color: #f56c6c;
}

.table-empty {
  padding: 36px 0;
  color: #c0c4cc;
  font-size: 13px;
}
.table-empty i {
  font-size: 36px;
  display: block;
  margin-bottom: 8px;
}

/* 详情弹窗 */
.cert-detail .detail-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: #f7f9fc;
  border-radius: 10px;
  margin-bottom: 20px;
}
.detail-head .el-tag {
  margin-left: auto;
}
.detail-name {
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
}
.detail-org {
  margin-top: 4px;
  font-size: 13px;
  color: #909399;
}
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 24px;
}
.detail-label {
  font-size: $font-size-base;
  color: #909399;
  margin-bottom: 4px;
}
.detail-value {
  font-size: 14px;
  color: #1f2d3d;
}

@media (max-width: 768px) {
  .summary-bar { gap: 20px; }
  .summary-tip { margin-left: 0; width: 100%; }
}
</style>
